import { Component, ErrorInfo, ReactNode } from 'react';
import { ErrorFallback, type ErrorFallbackSeverity } from './error/ErrorFallback';

interface Props {
  children: ReactNode;
  /** Custom fallback replaces the default ErrorFallback entirely. */
  fallback?: ReactNode;
  /** Controls the fallback variant: 'page' (full-height) or 'section' (compact inline). */
  severity?: ErrorFallbackSeverity;
  /** Called after an error is caught — use for telemetry / toast / parent notification. */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  componentStack?: string | null;
}

/**
 * Production-grade React Error Boundary.
 *
 * Catches render & lifecycle errors in any descendant component.
 * Renders a graceful fallback UI instead of a blank screen.
 *
 * Usage:
 *   <ErrorBoundary>                             // page-level (default)
 *   <ErrorBoundary severity="section">          // section / widget level
 *   <ErrorBoundary onError={logToBackend}>      // with custom telemetry
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);

    // Store component stack for copy-to-clipboard
    this.setState({ componentStack: errorInfo.componentStack ?? null });

    // Notify parent via callback
    this.props.onError?.(error, errorInfo);

    // Fire-and-forget: report to backend error log endpoint
    this.reportToBackend(error, errorInfo);
  }

  private reportToBackend(error: Error, errorInfo: ErrorInfo) {
    try {
      const payload = {
        message: error.message,
        stack: error.stack ?? null,
        componentStack: errorInfo.componentStack ?? null,
        url: window.location.href,
        timestamp: new Date().toISOString(),
      };

      // navigator.sendBeacon is non-blocking and survives page unloads
      if (typeof navigator.sendBeacon === 'function') {
        navigator.sendBeacon(
          '/api/logs/frontend-error',
          new Blob([JSON.stringify(payload)], { type: 'application/json' }),
        );
      } else {
        fetch('/api/logs/frontend-error', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => {
          // Swallow — logging should never throw a secondary error
        });
      }
    } catch {
      // Swallow
    }
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined, componentStack: null });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          onRetry={this.handleReset}
          severity={this.props.severity ?? 'page'}
          componentStack={this.state.componentStack}
        />
      );
    }

    return this.props.children;
  }
}
